import { create } from 'zustand';
import apiClient from '../lib/api';

export interface TaxRecord {
    id: string;
    tenantId: string;
    taxName: string;
    taxRate: number;
    amount: number;
    jurisdiction: string;
    filingStatus: string;
    filingDate?: string;
    createdAt: string;
    updatedAt: string;
}

interface TaxState {
    records: TaxRecord[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchRecords: (filters?: any) => Promise<void>;
    createRecord: (data: Partial<TaxRecord>) => Promise<void>;
    updateRecord: (id: string, data: Partial<TaxRecord>) => Promise<void>;
}

export const useTaxStore = create<TaxState>((set, get) => ({
    records: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
    },
    fetchRecords: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/money/tax', { params: filters });
            const { data, pagination } = response.data;
            set({ records: data, pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch tax records', isLoading: false });
        }
    },
    createRecord: async (data: Partial<TaxRecord>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/money/tax', data);
            const newRecord = response.data;
            set((state) => ({
                records: [newRecord, ...state.records],
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create tax record', isLoading: false });
            throw error;
        }
    },
    updateRecord: async (id: string, data: Partial<TaxRecord>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/money/tax/${id}`, data);
            const updatedRecord = response.data;
            set((state) => ({
                records: state.records.map((o) => (o.id === id ? updatedRecord : o)),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update tax record', isLoading: false });
            throw error;
        }
    },
}));
